import type { ReactNode } from 'react'
import { Link } from '@tanstack/react-router'
import type { FraudDecision, FraudMode, FraudOutcome, FraudSignal, FraudUserSummary } from '@/lib/api/fraud-types'
import { useAuth } from '@/lib/auth'
import { finixDisputeUrl, finixInstrumentUrl, finixPaymentUrl } from '@/lib/finix'
import { cn } from '@/lib/utils'
import { DECISION_META, EMPTY, MODE_META, OUTCOME_META, PILL, signalLabel, userDisplayName } from './format'

export function DecisionChip({ decision }: { decision: FraudDecision }) {
  const meta = DECISION_META[decision] ?? { label: decision, chip: DECISION_META.allow.chip }
  return <span className={cn(PILL, meta.chip)}>{meta.label}</span>
}

export function OutcomeChip({ outcome }: { outcome: FraudOutcome }) {
  const meta = OUTCOME_META[outcome] ?? { label: outcome, chip: OUTCOME_META.pending.chip }
  return <span className={cn(PILL, meta.chip)}>{meta.label}</span>
}

export function ModeChip({ mode }: { mode: FraudMode }) {
  const meta = MODE_META[mode] ?? MODE_META.shadow
  return <span className={cn(PILL, meta.chip)}>{meta.label}</span>
}

export function ScoreBadge({ score }: { score: number }) {
  const tone =
    score >= 90 ? 'bg-red-600 text-white'
      : score >= 40 ? 'bg-amber-500 text-white'
        : 'bg-neutral-200 text-neutral-800 dark:bg-neutral-700 dark:text-neutral-100'
  return (
    <span className={cn('inline-flex items-center justify-center min-w-[2.5rem] px-2 py-0.5 rounded-md text-sm font-bold tabular-nums', tone)}>
      {score}
    </span>
  )
}

const SIGNAL_PILL = 'inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-xs font-medium leading-4 whitespace-nowrap'
// A badge takes the tone of the decision it forces, matching the decision
// chips: an operator's step-up rule is amber like "3DS step-up", a hard
// decline is red like "Decline". Scored evidence stays neutral; a
// zero-weight note (a wallet token, a trusted account, a degraded policy)
// stays quiet.
const SIGNAL_TONE = {
  step_up: 'bg-amber-100 text-amber-800 dark:bg-amber-900/25 dark:text-amber-300',
  decline: 'bg-red-100 text-red-800 dark:bg-red-900/25 dark:text-red-300',
  evidence: 'bg-neutral-100 text-neutral-700 dark:bg-neutral-800 dark:text-neutral-300',
  muted: 'border border-neutral-200 bg-transparent text-neutral-500 dark:border-neutral-800 dark:text-neutral-400',
}

export function SignalBadge({ signal }: { signal: FraudSignal }) {
  const tone = signal.hard ? SIGNAL_TONE[signal.hard] : signal.weight ? SIGNAL_TONE.evidence : SIGNAL_TONE.muted
  return (
    <span className={cn(SIGNAL_PILL, tone)} title={signal.detail || undefined}>
      {signalLabel(signal.code)}
      {signal.weight ? <span className="tabular-nums opacity-60">+{signal.weight}</span> : null}
      {signal.hard === 'decline' ? (
        <span className="rounded-sm bg-red-200/80 px-1 text-[10px] font-semibold uppercase tracking-wide text-red-900 dark:bg-red-500/25 dark:text-red-200">
          Decline
        </span>
      ) : null}
    </span>
  )
}

export function SignalList({ signals, compact = false }: { signals: FraudSignal[]; compact?: boolean }) {
  if (!signals?.length) return <span className="text-neutral-400 dark:text-neutral-600">{EMPTY}</span>
  const shown = compact ? signals.slice(0, 3) : signals
  return (
    <ul className={cn('flex gap-1', compact ? 'flex-wrap' : 'flex-col gap-1.5')}>
      {shown.map((signal, index) => (
        <li key={`${signal.code}-${index}`} className={cn('flex items-baseline gap-2', compact ? '' : 'flex-wrap')} title={signal.detail}>
          <SignalBadge signal={signal} />
          {!compact && signal.detail && (
            <span className="text-sm text-neutral-600 dark:text-neutral-400">{signal.detail}</span>
          )}
        </li>
      ))}
      {compact && signals.length > shown.length && (
        <li className="self-center text-xs text-neutral-400">+{signals.length - shown.length} more</li>
      )}
    </ul>
  )
}

/**
 * A Finix id (transfer TRxxx / instrument PIxxx / dispute DIxxx) as a deep link into
 * EventPay's Finix dashboard, exactly as the transaction modal links a
 * charge id: staff only, and plain mono text for anyone else.
 */
export function FinixLink({ id, kind = 'payment', className }: {
  id?: string | null
  kind?: 'payment' | 'instrument' | 'dispute'
  className?: string
}) {
  const { user } = useAuth()
  const url = { payment: finixPaymentUrl, instrument: finixInstrumentUrl, dispute: finixDisputeUrl }[kind]
  const href = user?.is_superuser ? url(id) : null
  if (!href) return <span className={cn('font-mono text-xs', className)}>{id || EMPTY}</span>
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      title="Open in Finix dashboard"
      className={cn('font-mono text-xs text-blue-600 hover:underline dark:text-blue-400', className)}
    >
      {id}
    </a>
  )
}

export function Banner({ kind, children }: { kind: 'success' | 'error' | 'info'; children: ReactNode }) {
  const tone = {
    success: 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800 text-green-800 dark:text-green-200',
    error: 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800 text-red-700 dark:text-red-300',
    info: 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800 text-blue-800 dark:text-blue-200',
  }[kind]
  return <div className={cn('p-3 border rounded-lg text-sm', tone)}>{children}</div>
}

export function UserLink({ eventId, user, className }: { eventId: number; user: FraudUserSummary | null | undefined; className?: string }) {
  if (!user) return <span className="text-neutral-400">Unknown</span>
  return (
    <Link
      to="/events/$eventId/fraud/users/$userId"
      params={{ eventId: String(eventId), userId: String(user.id) }}
      className={cn('font-medium text-blue-600 dark:text-blue-400 hover:underline', className)}
    >
      {userDisplayName(user)}
    </Link>
  )
}

export function Spinner() {
  return <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
}
